<script lang="ts" setup>
import {
  BroadcastChannelEvent,
  initializeAuthenticationChannel,
  postMessageToAuthenticationChannel,
} from '@@/bits/auth_broadcast_channel'
import { getTabId } from '@@/bits/broadcast_channel'
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzPadletLogo from '@@/library/v4/components/OzPadletLogo.vue'
import SkipToContentLink from '@@/vuecomponents/SkipToContentLink.vue'
import { computed, onMounted } from 'vue'

const illustrationLightUrl = 'https://padlet.net/logged-out/v0_1/illustration.webp'
const ILLUSTRATION_ORIGINAL_IMAGE_WIDTH = 6912
const ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT = 1148

defineProps<{
  loginUrl: string
}>()

const illustrationHeight = computed((): number => {
  return 258
})

const illustrationWidth = computed((): number => {
  return illustrationHeight.value * (ILLUSTRATION_ORIGINAL_IMAGE_WIDTH / ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT)
})

onMounted(() => {
  initializeAuthenticationChannel({
    onLogin: () => {
      navigateTo('/dashboard')
    },
    onLogout: () => {
      navigateTo('/auth/login')
    },
  })

  postMessageToAuthenticationChannel({
    message: BroadcastChannelEvent.LoggedOut,
    sourceId: getTabId(),
  })
})
</script>

<template>
  <div :class="['relative', 'h-vh100', 'flex flex-col items-center', 'bg-[#ffebf3] dark:bg-[#2B2B2B]']">
    <header :class="['fixed flex items-center box-border w-full px-4', 'h-14 ps-5']">
      <SkipToContentLink />
      <OzPadletLogo :class="[{ invisible: device.electronApp }]" />
    </header>

    <main id="main-content" :class="['flex', 'flex-col', 'justify-center', 'h-full']" tabindex="-1">
      <div class="text-dark-text-100 dark:text-light-text-100 text-center">
        <h1 class="text-surface-title font-semibold px-2">
          {{ __('You’re logged out') }}
        </h1>
        <h2 class="text-login-body px-2 mt-2.5">
          {{ __('We’ll miss you!') }}
        </h2>
      </div>

      <div class="flex justify-center mt-5">
        <OzContainedButton
          data-testid="logBackInButton"
          :color-scheme="OzContainedButtonColorScheme.Primary"
          :text="__('Log back in')"
          :href="loginUrl"
        />
      </div>
    </main>

    <footer class="flex w-full h-[258px] desktop:h-auto">
      <ImageThumbnail
        :src="illustrationLightUrl"
        :original-image-height="ILLUSTRATION_ORIGINAL_IMAGE_HEIGHT"
        :original-image-width="ILLUSTRATION_ORIGINAL_IMAGE_WIDTH"
        :width="illustrationWidth"
        :height="illustrationHeight"
        resize-strategy="fit"
        :process-image="true"
        :lazy-loading="false"
        alt=""
      />
    </footer>
  </div>
</template>

<style lang="postcss">
@import '@@/styles/kit';
</style>
